import type { AppDispatch, RootState } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import {
  openRatingRequest,
  selectSection,
  setComponentSelection,
  setCurrentTab,
  setSelectedAccessory,
} from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { analyticsComponentName, track } from 'app/analytics';
import { ahuBoardBus } from './bus/ahuBoardBus';
import {
  beginCanvasRedrawBatch,
  holdCanvasRedrawBatchFor,
} from './three/canvasRedrawScheduler';
import { newlyAddedAccessoryIds } from './forms/accessory/useAccessoryForm';
import { defaultRowYOffset } from './components/hybridYOffset';
import { lugsOnForTunnel } from './hybridLugFields';
import {
  validateOnNewComponent,
  validateOnNewAccessory,
  validateTunnel,
  validateTunnelOnlyComponents,
} from './validation/validationController';

/**
 * Phase 6e1 — api.js controller port (§9.3): the auto-wired receivers of
 * `create:component:any` / `create:accessory:any` / `duplicate:accessory:any`
 * plus section creation. `fn['busy_tunnels']` stays a per-tunnel re-entrancy
 * flag only (no hidden queue — parity 173).
 */

/**
 * api.js default_section_length (:5-15) — legacy sends `length` in the section
 * POST (`section_length || default_section_length.plenum`, api.js:60). Without
 * it the server persists length NULL (AHUSection.length has no default) and
 * the section renders zero-length (QA batch 4 #1/#3).
 *
 * Exported because the hybrid Add Section dialog needs the same numbers to
 * seed its X-Offset column — legacy reads them off `api.default_section_length`
 * there too (insert_component.popup.js:213 `getAddedSectionLength`).
 */
export const DEFAULT_SECTION_LENGTH: Record<string, number> = {
  plenum: 24,
  fan: 60,
  coil: 12,
  filter: 12,
  eliminator: 12,
  gas_heater: 32,
  evap_pack: 36,
  silencer: 38,
  split_plenum: 24,
};

/**
 * Keyed by `MasterAHUComponent.value` — what the insert strip
 * (`/api/v1.0/cms/1/v1.0.0/ahu/components/`) actually puts in `component_value`,
 * and therefore what `createComponentAny` is handed. Legacy resolved the same
 * key through RequireJS (`component.<value>.model`, api.js:238) and read the
 * model's `urlRoot`; a miss here means NO component row is POSTed at all and the
 * inserted sections stay bare plenums.
 *
 * ★ Heat Wheel's value is `er_wheel`, NOT `energy_wheel` — the Q22 naming split.
 * `energy_wheel` is the *component* type the server derives back out
 * (models_component.py:62), and it is what `section.type` reads after a refetch,
 * but it is never a `component_value`. Legacy mapped
 * `component.er_wheel.model` → `/api/v1.0/ahu/energy_wheels/` (app.js:506,
 * energy_wheel.js:29); without the `er_wheel` key here the lookup fell through
 * and inserting a Heat Wheel produced two Plenum sections.
 */
const COMPONENT_PLURALS: Record<string, string> = {
  fan: 'fans',
  coil: 'coils',
  filter: 'filters',
  eliminator: 'eliminators',
  evap_pack: 'evap_packs',
  gas_heater: 'gas_heaters',
  silencer: 'silencers',
  er_wheel: 'energy_wheels',
  energy_wheel: 'energy_wheels',
  rotary: 'energy_wheels',
  plate_heat_exchanger: 'plate_heat_exchangers',
  plate: 'plate_heat_exchangers',
  uds: 'user_defined_components',
  user_defined_component: 'user_defined_components',
};

const ACCESSORY_PLURALS: Record<string, string> = {
  damper: 'dampers',
  louver: 'louvers',
  access_door: 'access_doors',
  opening: 'openings',
  hood: 'hoods',
  drain_pan: 'drain_pans',
  floor_drain: 'floor_drains',
  light: 'lights',
  vfd: 'vfds',
  jbox: 'jboxes',
  disconnect: 'disconnects',
  enclosure: 'enclosures',
  starter: 'starters',
  adp_gage: 'adp_gages',
  uvc: 'uvcs',
  dog_house: 'dog_houses',
  user_defined_accessory: 'user_defined_accessories',
  // Combo Louver (KA-5245) — A0.1. A combo louver IS a louver: the strip entry
  // `combo_louver` (seeded by cms/migrations/0083_seed_combo_louver_accessory.py,
  // inventory §7.1) is remapped to `louver` in createAccessoryAny below, exactly
  // as api.js:375-378 does, so this key is only reached by a caller that has NOT
  // remapped (duplicateAccessoryAny keys off the server `type`, always 'louver').
  // Present because the inventory's §7.3 build spec lists it as change 1 of 3;
  // on its own it would POST a PLAIN louver.
  combo_louver: 'louvers',
};

/** Per-tunnel re-entrancy guard (fn['busy_tunnels'] — §9.3, Q2 closed). */
const busyTunnels = new Set<number>();

/**
 * §9.3 api.accessory.duplicate `need_to_delete` strip.
 *
 * VERBATIM from `api.accessory.need_to_delete` (api/api.js:320-347). Every one
 * of these is DERIVED on the model and exposed by the serializer as a read-only
 * `@property`, so PATCHing it back at the clone is not merely redundant, it is a
 * 500: DRF's `ModelSerializer.update` does a plain `setattr` and Django raises
 * `AttributeError: property '<name>' of '<Model>' object has no setter`. That is
 * the reported Hood failure exactly (`face_area`); Access Door (`width_calc`)
 * and Drain Pan crash the same way.
 *
 * So the list is load-bearing and has to track legacy's. The first port kept 8
 * of the 41 entries, which is why the bug was invisible on a freshly added
 * accessory — every derived field is still null there, so the `v == null` filter
 * dropped it — and fired the moment the accessory had been configured enough for
 * the properties to compute.
 */
const DUPLICATE_STRIP_KEYS = [
  'id',
  'section_id',
  'apd',
  'position_id',
  'face_velocity',
  'distance_to_downstream_wall',
  'distance_to_roof',
  'distance_to_right_wall',
  'face_dim_1',
  'face_dim_2',
  'face_dim_3',
  'face_dim_4',
  'face_dim_5',
  'face_area',
  'door_ids',
  'unique',
  'door_width',
  'door_height',
  'clear_height',
  'clear_width',
  'width_calc',
  'height_calc',
  'casing_mtl_gauge',
  'brightness',
  'hood_id',
  'min_clear_if_on_end_wall_from_btm',
  'min_clear_if_on_end_wall_from_flr',
  'min_clear_if_on_end_wall_from_rf',
  'min_clear_if_on_end_wall_from_side_walls',
  'min_clear_if_on_end_wall_from_sides',
  'min_clear_if_on_end_wall_from_top',
  'min_clear_if_on_flr_from_downs',
  'min_clear_if_on_flr_from_section_ups_downs',
  'min_clear_if_on_flr_from_side_walls',
  'min_clear_if_on_flr_from_sides',
  'min_clear_if_on_flr_from_ups',
  'min_clear_if_on_rf_from_downs',
  'min_clear_if_on_rf_from_section_ups_downs',
  'min_clear_if_on_rf_from_side_walls',
  'min_clear_if_on_rf_from_sides',
  'min_clear_if_on_rf_from_ups',
  'base_height',
  'mounting_planes',
  // Not in legacy's list: response keys the DRF serializers add that legacy's
  // Backbone models never carried. (`clearances`, the read-only dict holding
  // the same values as the `min_clear_*` entries above, is deliberately NOT
  // stripped — legacy sends it and the serializer ignores it.)
  'section',
  'position',
  'label',
  'created',
  'updated',
  'created_at',
  'updated_at',
];

/**
 * Position keys legacy deletes before cloning (api.js:452-458), on top of the
 * shared drop-nulls pass. `auto_center_x`/`auto_center_y` and the
 * `mounting_plane`/`mounting_type` display strings are left at whatever the
 * fresh PositioningOnCabinet row was created with; only the real coordinates
 * (`x`, `y`, `mounting_plane_id`, `default_pos_*`) carry over.
 */
const DUPLICATE_POSITION_STRIP_KEYS = [
  'id',
  'auto_center_x',
  'auto_center_y',
  'mounting_plane',
  'mounting_type',
];

/** Hybrid AHU tunnel target (§4 Add-Section hybrid mode) — see hybridInsertionBindings.ts. */
export interface HybridTunnelTarget {
  row: number;
  level: number;
  position: number;
  index_id: number;
  existingTunnelId?: number;
  /**
   * Legacy's `addressing` argument (api.js:653) — "When `addressing` is
   * provided we always create a new tunnel at the explicit (row, level,
   * position)". Set for every hybrid Add-Section row that is not reusing a
   * specific tunnel: the same legacy `index_id` repeats across back-to-back
   * peers, and `position` is only a placeholder the backend renumbers, so an
   * address lookup would hand back the row's LEAD tunnel instead of creating
   * the peer the user asked for.
   */
  alwaysCreate?: boolean;
  /**
   * `addressing.peer_tunnel_id` (api.js:578-597) — an existing tunnel to copy
   * width/height (and airflow, when the dialog left it unset) from, so a new
   * back-to-back peer renders the same size as the chain it joins. Base is
   * deliberately NOT inherited; see the legacy comment at api.js:540-547.
   */
  peerTunnelId?: number;
  x_offset?: number;
  x_anchor_position?: number;
  airflow_direction_id?: number;
  insert_upstream_of_tunnel_id?: number;
}

export interface CreateComponentBlock {
  tunnel_index_id: number;
  component_name: string;
  component_value: string;
  component_type?: string;
  order?: number;
  isCtrlPressed?: boolean;
  /** Present for a hybrid-mode Add-Section row — routes tunnel resolution
   * through getOrCreateHybridTunnel instead of getOrCreateTunnelFromIndex. */
  hybrid?: HybridTunnelTarget;
}

interface BoardContext {
  ahuId: number;
}

const getCachedTunnels = (getState: () => RootState): IAhuBoardTunnel[] => {
  const queries = (getState()[ahuBoardApi.reducerPath] as {
    queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
  }).queries;
  for (const q of Object.values(queries)) {
    if (q?.endpointName === 'getAhuTunnelList' && Array.isArray(q.data)) {
      return q.data as IAhuBoardTunnel[];
    }
  }
  return [];
};

/**
 * Section types whose selector opens by itself once the section is inserted —
 * legacy tunnel.js:126-135, the `auto:select:component` handler:
 *
 *   if (current_section && (type === 'evap_pack' || type === 'filter') &&
 *       last_auto_rated_section_id !== current_section.id &&
 *       !document.querySelector('.main-rotation.rotate')) {
 *     $('#show_rating').click();
 *   }
 *
 * These two are the catalogue-selection types: the section is meaningless until
 * a media/filter is picked, so legacy pops the SELECTION dialog for you.
 */
const AUTO_OPEN_SELECTOR = ['evap_pack', 'filter'];

/**
 * The `$('#show_rating').click()` half of the chain above. Legacy reached it
 * over the syncdata socket (header.js:296-303 fires `auto:select:component` on
 * the ahusection POST, tunnel.js re-arms 200ms later); the React board has no
 * section socket listener, so createComponentAny — which already stands in for
 * that socket for the tunnel/section refetches — calls it directly.
 *
 * The body is FormsPanel.showRating's, for the same reason documented there:
 * React only mounts the ACTIVE tab's component form, and the form is the single
 * authority for the selector's component id, so we switch to the components tab
 * to mount it and let its own `rating.requestToken` effect fire the opener. The
 * request survives until the form mounts, so no timer is needed.
 *
 * `.main-rotation.rotate` = a selector is already open (ComponentSelectorDialog
 * owns that class); legacy declines to stack a second one and so do we.
 */
const autoOpenSelector = (
  dispatch: AppDispatch,
  getState: () => RootState,
  sectionId: number
): void => {
  const state = getState().ahuBoard;
  if (document.querySelector('.main-rotation.rotate')) return;
  if (state.selection.currentSectionId !== sectionId) return;
  if (state.selection.currentTab !== 'components') dispatch(setCurrentTab('components'));
  dispatch(openRatingRequest(sectionId));
};

/**
 * `api.tunnel.get_or_create_tunnel_from_index` (api.js:507-518) — resolve the
 * tunnel that owns a CMS tunnel index, CREATING it when the AHU has no tunnel
 * at that index yet. Legacy's contract is the whole point: the insert popup
 * lists every index the resulting configuration allows, including ones that are
 * not tunnels yet, so "add/duplicate into Tunnel 2" is also the gesture that
 * brings Tunnel 2 into existence.
 *
 * `created` is legacy's `!tunnel.is_not_new` (set on the found branch only) and
 * is what the duplicate POST sends as `new_tunnel` — the flag that makes
 * SectionDuplicateView (ahu/views.py:150-167) copy width/height/base/lug
 * attachment/floor/casing from the source tunnel onto the fresh one.
 *
 * Returns `null` when the tunnel POST fails; callers skip that block, as legacy
 * did by never resolving.
 */
export const getOrCreateTunnelFromIndex =
  (indexId: number, ctx: BoardContext) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<{ tunnel: IAhuBoardTunnel; created: boolean } | null> => {
    const found = getCachedTunnels(getState).find((t) => t.index_id === indexId);
    if (found) return { tunnel: found, created: false };
    try {
      await dispatch(
        ahuBoardFormsApi.endpoints.createAhuTunnel.initiate({
          ahu_id: ctx.ahuId,
          index_id: indexId,
        })
      ).unwrap();
    } catch {
      return null;
    }
    await dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ctx.ahuId, { forceRefetch: true })
    ).unwrap();
    const tunnel = getCachedTunnels(getState).find((t) => t.index_id === indexId);
    ahuBoardBus.emit('sections_collection:created', tunnel?.id);
    if (!tunnel) return null;
    return { tunnel, created: true };
  };

const findByAddress = (
  tunnels: IAhuBoardTunnel[],
  target: HybridTunnelTarget
): IAhuBoardTunnel | undefined =>
  tunnels.find(
    (t) =>
      (t.row ?? 1) === target.row &&
      (t.level ?? 1) === target.level &&
      (t.position ?? 1) === target.position
  );

/**
 * Hybrid AHU tunnel resolution (§4 Add-Section hybrid mode): reuse the
 * target's existing tunnel when the row already resolved one
 * (`existingTunnelId`, ordinary section growth), otherwise create it via
 * the backend's idempotent create-by-address / insert-upstream-of flow
 * (tunnel_viewset.py) — a concurrent duplicate POST for the same address
 * comes back 200 with the row that won, not a 400, so a client-side address
 * check is a fast-path only, not required for correctness.
 */
export const getOrCreateHybridTunnel =
  (target: HybridTunnelTarget, ctx: BoardContext) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<{ tunnel: IAhuBoardTunnel; created: boolean } | null> => {
    if (target.existingTunnelId != null) {
      const found = getCachedTunnels(getState).find((t) => t.id === target.existingTunnelId);
      if (found) {
        // Reusing a tunnel doesn't make the dialog's choices moot — its
        // Direction select and X-Offset input default to the tunnel's current
        // values but the user is free to change them before pressing INSERT.
        // AWAITED, not fire-and-forget: the caller creates the section on this
        // tunnel immediately after, and two independent in-flight requests
        // have no ordering guarantee, so the section would otherwise be
        // created against a still-stale x_offset (api.js:670-709).
        const updates: Partial<IAhuBoardTunnel> = {};
        if (
          target.airflow_direction_id != null &&
          Number(found.airflow_direction_id) !== target.airflow_direction_id
        ) {
          updates.airflow_direction_id = target.airflow_direction_id;
        }
        if (target.x_offset != null && Number(found.x_offset ?? 0) !== target.x_offset) {
          updates.x_offset = target.x_offset;
        }
        if (Object.keys(updates).length) {
          try {
            await dispatch(
              ahuBoardApi.endpoints.patchAhuTunnel.initiate({
                id: found.id,
                ahuId: ctx.ahuId,
                data: updates,
              })
            ).unwrap();
          } catch {
            /* PATCH refused — fall through with the tunnel as it stands */
          }
        }
        return { tunnel: found, created: false };
      }
    }
    if (!target.alwaysCreate) {
      const existingByAddress = findByAddress(getCachedTunnels(getState), target);
      if (existingByAddress) return { tunnel: existingByAddress, created: false };
    }

    const cached = getCachedTunnels(getState);
    // `position` is only a placeholder — the backend's auto-address post_save
    // signal renumbers the whole (row, level) chain by physical order once the
    // new row lands. But it MUST NOT collide with a peer that is already
    // there: tunnel_viewset.create's dedup guard returns the existing tunnel
    // at (ahu, row, level, position) with a 200 instead of creating, so a
    // hard-coded 1 makes every back-to-back insert silently land back on the
    // chain's lead tunnel. Legacy picks `peers + 1` for exactly this reason
    // (api.js:568-577).
    const peersAtRowLevel = cached.filter(
      (t) => (t.row ?? 1) === target.row && (t.level ?? 1) === target.level
    );
    const peer =
      target.peerTunnelId != null
        ? cached.find((t) => t.id === target.peerTunnelId)
        : undefined;
    /** Tunnel dimensions come back as strings on some serializers. */
    const num = (v: number | string | null | undefined): number | undefined => {
      const n = Number(v);
      return v == null || v === '' || !Number.isFinite(n) ? undefined : n;
    };

    // A tunnel that STARTS a row (no peers at this row:level yet, row > 1) is
    // the one case where the model's `centered` Y-Offset draws the new row on
    // top of the row it should sit beside — hybridYOffset.ts has the why.
    const rowYOffset =
      !peersAtRowLevel.length
        ? defaultRowYOffset({
            row: target.row,
            level: target.level,
            // The width the create below sends, so the offset matches the
            // tunnel that actually lands.
            width: num(peer?.width) ?? 0,
            upstreamEdgeX: target.x_offset ?? 0,
            tunnels: cached,
          })
        : null;

    let createdId: number | undefined;
    try {
      const created = await dispatch(
        ahuBoardFormsApi.endpoints.createAhuTunnel.initiate({
          ahu_id: ctx.ahuId,
          index_id: target.index_id,
          row: target.row,
          level: target.level,
          position: target.insert_upstream_of_tunnel_id
            ? target.position
            : peersAtRowLevel.length + 1,
          x_offset: target.x_offset ?? 0,
          x_anchor_position: target.x_anchor_position ?? 0,
          tunnel_offset_direction: rowYOffset?.tunnel_offset_direction,
          tunnel_offset: rowYOffset?.tunnel_offset,
          // api.js:588-597 — the dialog's own pick wins; the peer only fills
          // in what the dialog left unset.
          airflow_direction_id:
            target.airflow_direction_id ?? peer?.airflow_direction_id ?? undefined,
          width: num(peer?.width),
          height: num(peer?.height),
          insert_upstream_of_tunnel_id: target.insert_upstream_of_tunnel_id,
        })
      ).unwrap();
      const id = Number(created?.id);
      if (Number.isFinite(id)) createdId = id;
    } catch {
      return null;
    }
    await dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ctx.ahuId, { forceRefetch: true })
    ).unwrap();
    // Prefer the id the POST returned: `position` we sent is a placeholder the
    // backend's auto-address signal renumbers, so re-finding by address can
    // land on a peer rather than the row just created. The address fallback is
    // only safe when we were NOT adding a peer to an occupied chain — there it
    // would resolve to the chain's lead tunnel and the section would be
    // appended to it, which is the very bug this path exists to avoid.
    const rows = getCachedTunnels(getState);
    const tunnel =
      (createdId != null ? rows.find((t) => t.id === createdId) : undefined) ??
      (peersAtRowLevel.length ? undefined : findByAddress(rows, target));
    ahuBoardBus.emit('sections_collection:created', tunnel?.id);
    if (!tunnel) return null;
    return { tunnel, created: true };
  };

/**
 * The lug POST legacy's `api.tunnel.create` (api.js:496-502) fires 3s after a
 * new tunnel comes back carrying a Base. Split out of createComponentAny so the
 * duplicate-section flow gets it too — without it a tunnel created by
 * duplicating into it draws no lifting lugs.
 */
export const createLugsForNewTunnel =
  (tunnelId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    await dispatch(ahuBoardFormsApi.endpoints.createTunnelLug.initiate({ tunnel_id: tunnelId }))
      .unwrap()
      // Re-GET rather than seed the cache from the POST body. The POST answers
      // with a plain AHULugSerializer payload — the TUNNEL-owned AHULug — and on
      // a hybrid AHU the lugs hang off the shared Base Groups instead
      // (`_write_lug_positions` keys them on `base_group`, base_group_lug.py:214),
      // so that row's `positions` is empty and seeding the cache with it told
      // the canvas the tunnel has no lugs. Only the placement GET returns the
      // hybrid `{hybrid, valid_kinds, positions}` shape `getTunnelLugs` reads
      // (AHUTunnelLugViewSet.retrieve). Nothing else refetched it, so the lugs
      // stayed invisible until a reload re-ran the boot GET. Legacy hit exactly
      // this and fixed it the same way — `await model.fetch()` in
      // `LugCollection.createModel` (collection/tunnel.lugs.js, KA-x 29ec919e25
      // "Fix hybrid AHU new-tunnel lugs invisible until page refresh"), which
      // also added the whole-canvas redraw below: the per-tunnel one is a no-op
      // while the new tunnel is still missing from the canvas's tunnel models.
      .then(() =>
        dispatch(
          ahuBoardApi.endpoints.getTunnelLugPlacement.initiate(tunnelId, {
            forceRefetch: true,
            subscribe: false,
          })
        ).unwrap()
      )
      .then((placement) => {
        // Same hand-off as refetchHybridLugPlacements below: the
        // pane's mirror of this tunnel is the 472-byte body it read a second
        // ago, between the tunnel POST and its first section. Without this it
        // keeps it until a reload.
        ahuBoardBus.emit(
          'tunnelLugs:placementRefetched',
          tunnelId,
          placement as unknown as Record<string, unknown>
        );
        ahuBoardBus.emit('redraw:tunnel:lugs', tunnelId);
        ahuBoardBus.emit('redraw:canvas');
      })
      .catch(() => undefined);
  };

/**
 * A hybrid AHU's lifting lugs are laid out SERVER-side and re-laid on every
 * base-group recompute. Adding a section changes the tunnel's length, so the
 * section POST's own request rebuilds the side Base Groups and
 * `sync_hybrid_lugs` (base_group_lug.py:239) writes the lug run across the new
 * footprint. Measured on the reported AHU 6704: the section POST writes two
 * positions per side group, and `GET ahu/tunnel_lugs/9269/` goes from 1024 to
 * 3551 bytes across it.
 *
 * Nothing on the client re-read that. The placement is fetched once at boot
 * (`useAhuBoardBootstrap`) and afterwards only on the Lifting Lugs pane's own
 * gestures, so the canvas kept drawing the pre-section placement — for a
 * tunnel whose first section had just arrived, no lugs at all — until a reload
 * re-ran the boot GET. That is the reported "lugs appear only after refreshing
 * the page".
 *
 * EVERY lug-bearing tunnel, not just the one the section landed in. The
 * re-lay is AHU-wide in both directions: `sync_hybrid_lugs` walks
 * `find_lug_runs(ahu_id)` (base_group_lug.py:294), so a run spanning several
 * tunnels' base groups is recomputed and rewritten whole; and
 * `tunnel_positions` (:333) then attributes each position to a tunnel by
 * whether it falls inside that tunnel's span, so growing one tunnel moves the
 * ones downstream of it and reassigns lugs between them even where the
 * positions themselves did not change. Refetching only the tunnel that got the
 * section left its neighbours drawing the run's old geometry — the reported
 * "lugs are rendered on the adjacent bases too, and only a refresh removes
 * them". Measured on AHU 6705 across two section adds: `tunnel_lugs/9271`
 * 3551 → 2010 bytes and `tunnel_lugs/9273` 2014 → 474 with neither tunnel
 * touched by the gesture, and nothing re-reading either until the reload.
 *
 * Legacy takes the same all-tunnels fork for the same reason, in the one place
 * it refetches at all — `refetchLugsAfterSettingsChange`
 * (tab.lifting.lug.js:274-283): "For a hybrid AHU the setting just changed
 * applies to every tunnel, so every tunnel with a base needs its positions
 * refetched -- not just the active one."
 *
 * Hybrid only: a standard AHU's lugs hang off the tunnel's own AHULug, so only
 * the tunnels the gesture touched can have moved and
 * `refetchLugPlacementsAfterGeometryChange` below re-reads exactly those. (Until
 * `sync_standard_lugs` the server did not re-lay those at all behind a section
 * write, and the client's whole contract there was legacy's ReCalculate button
 * — `LiftingLugPane` arms on `tunnelLugs:recalcPlacement` and refetches on
 * `recalc:lug:placement`. That button is still what a change the server does
 * NOT follow — an accessory moving into a lug's footprint — goes through.)
 *
 * A tunnel WIDTH/HEIGHT edit is the same class of gesture and takes the same
 * fork. `AHUTunnels.save()` ends in `recompute_base_groups(ahu_id)`
 * (models_tunnel.py:2125), which ends in `sync_hybrid_lugs`
 * (base_group_orchestration.py:135), so the PATCH re-lays the runs before it
 * answers — width sets the tunnel's y-span (tunnel_geometry.py:410), which is
 * the side groups' `coord` and the upstream/downstream groups' extent.
 * Measured on the reported AHU 6711, narrowing tunnel 9297 from 100" to 80":
 * `tunnel_lugs/9297` 2014 → 3553 bytes and its untouched neighbour
 * `tunnel_lugs/9296` 474 → 2014, neither re-read until the reload. What the
 * client did instead was `tunnelLugs:recalcPlacement`, which takes the lugs
 * off the canvas and arms ReCalculate — correct on a standard AHU, where the
 * server has not moved anything and the button is what moves it, but on hybrid
 * it hides a placement the server had already corrected. That is the reported
 * "its lugs are removed from the canvas … only after refreshing the page".
 *
 * An AIRFLOW DIRECTION flip takes the fork too, for two reasons that are not
 * the width one. First, `valid_kinds` is direction-dependent at READ time:
 * `_translate_kind` (lifting_lug_viewset.py:21-32) mirrors upstream↔downstream
 * and left↔right for a `right_to_left` tunnel, so the same unmoved base groups
 * are named differently the moment the flip lands. Measured on the reporter's
 * AHU 6715, flipping tunnel 9309: `valid_kinds` ['downstream','left','right'] →
 * ['left','right','upstream'], which is the Customize Lugs kind picker reading
 * "Downstream" for a tunnel the server now calls upstream. Second, the flip
 * re-bases `x_offset` onto the other edge (`airflowFlipPatch`), which is enough
 * to reorder the chain — `_auto_address_hybrid_peers` renumbers `position`,
 * `position` is one of `_GROUP_RELEVANT_FIELDS`, and a recompute that rebuilds
 * a group makes it `is_new`, which is the one case `sync_hybrid_lugs` re-lays
 * even with `ahu.lug_defaults` off (base_group_lug.py:291).
 *
 * All three airflow affordances take it — the `#tunnels-indexes` pill
 * (TunnelIndexes), the Configure Tunnel toggle (ConfigureTunnel) and the canvas
 * arrow (AhuCanvas) — since they share one PATCH body and one server effect.
 *
 * `hideWhilePending` reproduces the §19.2 half of that emit that is still
 * right on hybrid: take the lugs down for the length of the re-read, so the
 * redraw the PATCH success triggers never paints them at their stale position.
 */
/**
 * Force-read a set of tunnels' placements and hand each body to BOTH consumers
 * — the canvas (via the cache write the forced GET makes) and the Lifting Lugs
 * pane (via `tunnelLugs:placementRefetched`, which the pane's local mirror
 * needs; see the note in the hybrid fork below). Shared by that fork and the
 * standard per-tunnel one so the two cannot drift.
 */
const refetchPlacements = async (
  dispatch: AppDispatch,
  targets: number[],
  hideWhilePending: boolean
): Promise<void> => {
  if (hideWhilePending) {
    // Same two steps the pane's `tunnelLugs:recalcPlacement` handler takes
    // (`markPlacementStale` + a removing redraw) — the cache flag the canvas
    // reads (AhuCanvas getTunnelLugs → canvas3dGeometry:6538), then the
    // redraw that clears the meshes. The forced GET below replaces the whole
    // cache entry, flag included, so nothing has to unset it.
    targets.forEach((id) => {
      dispatch(
        ahuBoardApi.util.updateQueryData('getTunnelLugPlacement', id, (draft) => {
          draft.hide_lugs = true;
        })
      );
      ahuBoardBus.emit('redraw:tunnel:lugs', id, true);
    });
  }
  await Promise.all(
    targets.map((id) =>
      dispatch(
        ahuBoardApi.endpoints.getTunnelLugPlacement.initiate(id, {
          forceRefetch: true,
          subscribe: false,
        })
      )
        .unwrap()
        .then((placement) => {
          ahuBoardBus.emit(
            'tunnelLugs:placementRefetched',
            id,
            placement as unknown as Record<string, unknown>
          );
          ahuBoardBus.emit('redraw:tunnel:lugs', id);
        })
        .catch(() => undefined)
    )
  );
};

export const refetchHybridLugPlacements =
  (ahuId: number, { hideWhilePending = false }: { hideWhilePending?: boolean } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const ahu = ahuBoardApi.endpoints.getAhuUnit.select(ahuId)(getState()).data;
    if (!ahu?.is_hybrid) return;
    // Join the tunnel-list read the callers kick off alongside this one (RTK
    // Query dedupes by endpoint+arg, so this is that same request, not a
    // second one). The insert may have created the tunnel it filled, and the
    // server recomputes lengths for the rest — so the gate below has to run
    // against the post-insert list, not the one the board booted with.
    await dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, { subscribe: false })
    )
      .unwrap()
      .catch(() => undefined);
    const tunnels = getCachedTunnels(getState);
    // Same gate the boot feed uses: a tunnel with no Base, or with Include
    // Lifting Lugs off, has no placement to GET (and 404s if asked).
    const targets = tunnels
      .filter((tunnel) => tunnel.base_id != null && lugsOnForTunnel(ahu, tunnel))
      .map((tunnel) => tunnel.id);
    if (!targets.length) return;
    // The hand-off inside reaches the Lifting Lugs pane as well, not just the
    // canvas. The pane mirrors each tunnel's placement in local state and only
    // ever refills it on its own gestures, so everything the placement feeds
    // stayed at whatever was true when that tunnel was FIRST read — for a
    // tunnel the insert had just created, one second old and with no section
    // in it yet. Most visibly that is `valid_kinds`, the Customize Lugs kind
    // picker: measured on the reporter's AHU 6707, tunnel 9282 was read at 472
    // bytes before its section POST, and the 474-byte body this refetch got a
    // second later never reached the pane. Only a reload, which re-seeds the
    // mirror from the boot GET, corrected the options.
    await refetchPlacements(dispatch, targets, hideWhilePending);
  };

/**
 * A GEOMETRY CHANGE — a section added, deleted or resized, or a tunnel's
 * width/height edited — on either configuration. Hybrid takes the AHU-wide fork
 * above; a standard AHU re-reads only the tunnels the gesture actually touched,
 * because its lugs hang off the tunnel's own AHULug and no other tunnel can
 * have moved.
 *
 * Every one of those re-lays the placement SERVER-side before it answers
 * (`sync_standard_lugs` / `sync_hybrid_lugs` off the section and tunnel
 * signals), so the client's job is only to ask again. Called for all of them
 * whichever way the lugs run: a length change moves along-length lugs along
 * their rail AND moves the cross rail (0 / length) the along-width ones sit on,
 * and a width change does the mirror image, so there is no case where the
 * stored rows can have moved and this should be skipped.
 *
 * What it replaces differs by gesture. An add or a delete emitted no
 * `tunnelLugs:recalcPlacement` at all (legacy only does so from
 * `storage:section.model:change`, tunnel.js:118-130, a length PATCH), so the
 * ReCalculate button stayed disabled and nothing asked — the reported "lugs
 * appear only after refreshing the page when a new section is added", and the
 * delete leaving the old run drawn beside a tunnel that has since shrunk. A
 * length or width edit DID emit it, which took the lugs off the canvas and
 * armed the button: correct while the server left the rows alone, but now it
 * asks the user to press a button for work the write already did. The button
 * keeps the changes the server does NOT follow — an accessory moving into a
 * lug's footprint (`_build_avoid_zones`), which is still its own emit.
 *
 * `hideWhilePending` keeps the visible half of that arming for the gestures
 * that had it: the lugs come off the canvas for the length of the re-read, so
 * no redraw in between paints them at their old position.
 *
 * It has to be a GET, not just a redraw. The positions are stored rows, and
 * the only two things that rewrite them on a standard AHU are the section
 * write itself (`sync_standard_lugs`, which the section's own signals reach)
 * and the placement read, which lays them out lazily:
 * `AHUTunnelLugViewSet.retrieve`'s non-hybrid branch
 * (lifting_lug_viewset.py:606-613) saves the tunnel and then its `AHULug`.
 * Either way the browser is holding the pre-gesture body until it asks again.
 * Measured on AHU 6746: boot read of `tunnel_lugs/9381` 1024 bytes with the
 * tunnel still empty, the section POST behind it producing no lug request at
 * all, and the four positions only written when the pane was opened 11s later
 * (4077 bytes).
 */
export const refetchLugPlacementsAfterGeometryChange =
  (
    ahuId: number,
    touchedTunnelIds: number[],
    { hideWhilePending = false }: { hideWhilePending?: boolean } = {}
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const ahu = ahuBoardApi.endpoints.getAhuUnit.select(ahuId)(getState()).data;
    if (ahu?.is_hybrid) {
      await dispatch(refetchHybridLugPlacements(ahuId, { hideWhilePending }));
      return;
    }
    if (!touchedTunnelIds.length) return;
    // Join the tunnel-list read the callers kick off alongside this one (RTK
    // Query dedupes by endpoint+arg, so this is that same request): the gate
    // below reads `base_id`, which a tunnel the insert created only carries in
    // the post-insert list.
    await dispatch(ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, { subscribe: false }))
      .unwrap()
      .catch(() => undefined);
    const wanted = new Set(touchedTunnelIds);
    // Same gate the boot feed uses: a tunnel with no Base, or with Include
    // Lifting Lugs off, has no placement to GET (and 404s if asked).
    const targets = getCachedTunnels(getState)
      .filter((tunnel) => wanted.has(tunnel.id) && tunnel.base_id != null)
      .filter((tunnel) => lugsOnForTunnel(ahu, tunnel))
      .map((tunnel) => tunnel.id);
    if (!targets.length) return;
    await refetchPlacements(dispatch, targets, hideWhilePending);
  };

/**
 * "Align All Sections in X Direction" — the hybrid half of the §8.6 toolbar
 * button (CanvasToolbar; a standard AHU keeps legacy's `alignAhuTunnels`
 * length-equalize, which is a different operation and stays untouched).
 *
 * Unlike the legacy equalize, none of the arithmetic is client-side: the whole
 * layout solve lives in `tunnel_x_alignment.compute_x_alignment` and the POST
 * applies it. So this is a cache-refresh problem, and it is the WHOLE-UNIT
 * kind — the server moves an arbitrary set of tunnels in one request and
 * answers with nothing but their ids:
 *
 *  - `x_offset` on every moved tunnel, which `_auto_address_hybrid_peers`
 *    then uses to renumber `position` across each (row, level) group — the
 *    same gap `refetchTunnelsAfterAddressingChange` exists for, except there
 *    is no PATCH body here to detect it from;
 *  - `base_id`, because `recompute_base_groups` runs once at the end and
 *    bases are shared across the tunnels that sit on them;
 *  - the lifting-lug runs, since `recompute_base_groups` ends in
 *    `sync_hybrid_lugs` and `tunnel_positions` reattributes positions to
 *    tunnels by geometric span — so lugs move BETWEEN tunnels, including
 *    tunnels this call never moved.
 *
 * Hence a forced whole-list re-GET plus the AHU-wide lug fork, both inside one
 * canvas batch so the board redraws once off final state rather than once per
 * landing response. `hideWhilePending` takes the lugs down for the length of
 * the re-read, so the redraw never paints them at their pre-align position.
 *
 * An empty `moved_tunnel_ids` means the unit was already aligned — nothing was
 * written, so nothing is refetched and the canvas is left alone.
 */
export const alignHybridTunnelsX =
  (ahuId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const releaseCanvas = beginCanvasRedrawBatch();
    try {
      const movedIds = await dispatch(
        ahuBoardApi.endpoints.alignAhuTunnelsX.initiate(ahuId)
      )
        .unwrap()
        .then((res) => res?.moved_tunnel_ids ?? [])
        .catch(() => null);
      if (movedIds == null || !movedIds.length) return;
      await dispatch(
        ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, {
          forceRefetch: true,
          subscribe: false,
        })
      )
        .unwrap()
        .catch(() => undefined);
      await dispatch(refetchHybridLugPlacements(ahuId, { hideWhilePending: true }));
      // Every moved tunnel's span changed, and tunnel validation is span-based
      // (the same re-validate a single X-Offset edit does — TunnelCabinetRow
      // changeOffsetField).
      await Promise.all(movedIds.map((id) => dispatch(validateTunnel(id))));
      ahuBoardBus.emit('redraw:canvas');
    } finally {
      releaseCanvas();
    }
  };

/**
 * api.component.create (§9.3 — create:component:any auto-wire).
 *
 * The canvas is held for the whole insert (canvasRedrawScheduler): a section
 * POST, a tunnel-list re-GET, a component POST, the type/name stamp and a
 * section-list re-GET each land in their own tick, and each used to run its
 * own full geometry pass — so a new Filter was drawn as a default-length
 * "Plenum", redrawn as the tunnel grew, redrawn again once the stamp landed,
 * and only then settled. Legacy is one pass (api.js:246-251 stamps the section
 * and fires `show:on:canvas` once), and so is this now.
 */
export const createComponentAny =
  (blocks: CreateComponentBlock[], ctx: BoardContext) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const releaseCanvas = beginCanvasRedrawBatch();
    try {
      await dispatch(runCreateComponentAny(blocks, ctx));
    } finally {
      releaseCanvas();
    }
  };

const runCreateComponentAny =
  (blocks: CreateComponentBlock[], ctx: BoardContext) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    let firstSection: IAhuBoardSection | null = null;
    const createdSections: IAhuBoardSection[] = [];
    const newBasedTunnelIds: number[] = [];

    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];
      const resolved = await dispatch(
        block.hybrid
          ? getOrCreateHybridTunnel(block.hybrid, ctx)
          : getOrCreateTunnelFromIndex(block.tunnel_index_id, ctx)
      );
      if (!resolved) continue;
      const { tunnel } = resolved;
      if (resolved.created && tunnel.base_id) newBasedTunnelIds.push(tunnel.id);
      if (busyTunnels.has(tunnel.id)) continue;
      busyTunnels.add(tunnel.id);
      try {
        // api.section.create (api.js:58-64) WIRE-body parity (QA batch 9):
        // Backbone put the model default tag:'' on every create POST (section
        // model defaults:17-18) — omitting it persisted NULL; sitting_on_id/
        // split ship explicitly like legacy (align_tunnel.js:304-311).
        // order_rate keeps its omitted-when-undefined semantics
        // (JSON.stringify drops undefined keys).
        const body: Record<string, unknown> = {
          tag: '',
          tunnel_id: tunnel.id,
          length:
            DEFAULT_SECTION_LENGTH[block.component_value] ??
            DEFAULT_SECTION_LENGTH.plenum,
          order_rate: block.order ?? undefined,
          sitting_on_id: i > 0 && firstSection ? firstSection.id : null,
          split: block.component_value === 'split_plenum',
        };
        const section = await dispatch(
          ahuBoardFormsApi.endpoints.createAhuSection.initiate(body)
        ).unwrap();
        createdSections.push(section);
        if (!firstSection) firstSection = section;
        ahuBoardBus.emit('storage:section.model:create', {
          section,
          sections: createdSections,
        });
      } catch {
        /* creation failed — note classes are CSS-side */
      } finally {
        busyTunnels.delete(tunnel.id);
      }
    }

    // `api.tunnel.create` (api.js:496-502) followed a tunnel POST with
    // `LugCollection.createModel(tunnel.id)` whenever the new tunnel came back
    // with a Base — that POST is what gives the tunnel its AHULug row, and
    // without it the placement GET has nothing to return, so the new tunnel
    // draws no lugs and the Lifting Lug pane lists none. React kept legacy's
    // `redraw:tunnel:lugs` (which createModel emits) but not the POST in front
    // of it. Runs after the section loop, not inside it, for the reason legacy
    // waited 3s: the server lays positions out from the tunnel's length and
    // sections, so a lug row created before the first section lands is empty.
    // The response is the placement body, so seed the query cache the canvas
    // reads from (AhuCanvas getTunnelLugs) rather than re-GETting it.
    await Promise.all(
      newBasedTunnelIds.map((tunnelId) => dispatch(createLugsForNewTunnel(tunnelId)))
    );

    if (!firstSection) return;
    // Only the insert strip / insert-section popup reach this thunk (via the
    // create:component:any bus), so code-driven section POSTs are not counted.
    track('section_added', {
      section_type: analyticsComponentName(blocks[0].component_value),
      hybrid: !!blocks[0].hybrid,
    });
    // Tunnel lengths were recalculated server-side by the insert (section_
    // viewset move_all_right + save). Legacy gets the fresh tunnel row via the
    // syncdata socket (model/tunnel.js → `change:tunnel:dimensions`); the React
    // board has no tunnel socket listener, so refetch the tunnel list — the
    // cache write cascades into the canvas props (same observable re-render).
    // Deliberately NOT awaited (the component POST must not queue behind a
    // GET), but the canvas pass does wait for it, so the single draw carries
    // the server's recalculated tunnel length instead of correcting it after.
    holdCanvasRedrawBatchFor(
      dispatch(
        ahuBoardApi.endpoints.getAhuTunnelList.initiate(ctx.ahuId, {
          forceRefetch: true,
        })
      )
    );
    // The section the server just accepted re-laid the AHU's lug placement —
    // on hybrid in the section POST itself, on a standard AHU in the placement
    // GET this fires. Held like the tunnel re-GET above so the one canvas pass
    // carries the new lugs instead of a reload being needed to see them.
    // `newBasedTunnelIds` are excluded: createLugsForNewTunnel above already
    // POSTed their AHULug and re-read the placement, and it ran after the
    // section loop precisely so that read saw the section.
    holdCanvasRedrawBatchFor(
      dispatch(
        refetchLugPlacementsAfterGeometryChange(
          ctx.ahuId,
          Array.from(
            new Set(
              createdSections
                .map((section) => section.tunnel_id)
                .filter((id) => Number.isFinite(id) && !newBasedTunnelIds.includes(id))
            )
          )
        )
      )
    );
    const value = blocks[0].component_value;
    const isPlenum = value === 'plenum' || value === 'split_plenum';

    // First block sets viewdata (§9.3 — incl. the Plate/Rotary id+1 quirk).
    dispatch(
      selectSection({
        sectionId: firstSection.id,
        tab: 'sections',
        type: value,
        selectedSectionIds: blocks[0].isCtrlPressed
          ? [...getState().ahuBoard.selection.selectedSectionIds, firstSection.id]
          : [firstSection.id],
      })
    );
    ahuBoardBus.emit('update:multiple:sections:list');

    if (isPlenum) {
      // api.js:222-228 stamps component_name/component_id on viewdata INSIDE
      // the per-block loop, so plenum and split_plenum get it too — the
      // `component_value != 'plenum' && != 'split_plenum'` guard that skips the
      // component POST only comes afterwards. React stamped it on the component
      // path alone, so the tag heading of a Split-Plenum fell through to the
      // server's AHUSection.name ('Split-Plenum', models_section.py:147-150)
      // where legacy shows the toolbar's 'Plenum' (toolbar.components.js:140).
      // name_on_canvas is 'Plenum'/'Split-Plenum' here, never Plate/Rotary, so
      // neither id takes the §9.3 +1 quirk.
      dispatch(
        setComponentSelection({
          componentName: blocks[0].component_name,
          componentId: firstSection.id,
          componentSittingId: firstSection.id,
          type: value,
        })
      );
      void dispatch(validateTunnelOnlyComponents({ tunnelId: firstSection.tunnel_id }));
      ahuBoardBus.emit('show:on:canvas', {
        section: firstSection,
        pseudo: true,
        value,
      });
      return;
    }

    const plural = COMPONENT_PLURALS[value];
    if (!plural) return;
    try {
      const componentBody: Record<string, unknown> = { section_id: firstSection.id };
      if (createdSections[1]) componentBody.section_2_id = createdSections[1].id;
      const component = await dispatch(
        ahuBoardFormsApi.endpoints.createConcreteRow.initiate({
          plural,
          body: componentBody,
        })
      ).unwrap();
      track('component_added', { component: analyticsComponentName(value) });
      // A dual-tunnel component POST can REWRITE the second tunnel's row, and
      // the one it rewrites is load-bearing for the drawing. AHUTunnels.save
      // (models_tunnel.py:1592-1605) gives a brand-new stacked tunnel the
      // OPPOSITE airflow of the one below it — a fresh 1_2 comes back
      // `right_to_left` (id 2) from the tunnel POST above. A plate heat
      // exchanger runs both streams the same way, so plate_exchanger_viewset
      // .py:134-137 copies section 1's tunnel's airflow onto section 2's
      // tunnel and saves it — server-side only, inside THIS request.
      //
      // Legacy learned the new value from the syncdata socket (the tunnel's
      // post_save re-serializes over AMQP into the Backbone model) before it
      // drew anything. The React board has no tunnel socket listener, so
      // without this refetch the cached tunnel keeps airflow 2 for the rest of
      // the session and everything downstream reads it: the canvas draws the
      // stacked tunnel's sections REVERSED (Heatex lands downstream of the
      // Plate it sits on instead of over it), and Align Tunnels puts its
      // padding plenum on the wrong side of the Heatex — `adjustTunnelPart`
      // (alignTunnels.ts) keys the upstream/downstream choice off the same
      // field. AWAITED, because `show:on:canvas` below redraws from it.
      if (createdSections.length > 1) {
        await dispatch(
          ahuBoardApi.endpoints.getAhuTunnelList.initiate(ctx.ahuId, {
            forceRefetch: true,
          })
        );
      }
      const componentId = Number(component.id);
      dispatch(
        setComponentSelection({
          // api.js:224-225 keys both ids off the SECTION id, not the component
          // id (`section.id`, and `section.id + 1` for the Plate/Rotary quirk,
          // §9.3) — component.id is a different sequence entirely. Harmless
          // while nothing read these back; TagStrip now does, and a component
          // id that happened to collide with some section's id would put this
          // component's name on that section's tag heading.
          componentName: blocks[0].component_name,
          componentId:
            value === 'plate_heat_exchanger' || value === 'rotary' || value === 'plate'
              ? firstSection.id + 1
              : firstSection.id,
          componentSittingId: firstSection.id,
          type: value,
        })
      );
      // api.js:253-255 — legacy stamps the just-created section models with the
      // component BEFORE `show:on:canvas`, because the section POST response
      // still says 'plenum' (AHUSection.type is derived from its components and
      // the component row is younger than the section). Without this the canvas
      // and the section form both label a new Filter/Fan/Coil "Plenum".
      // NOTE: `createdSections` holds the mutation responses, which live in the
      // RTKQ cache and are Immer-frozen — stamping them in place throws
      // (silently, into the catch below, killing every emit after it). Stamp a
      // copy for the bus payload and the cache row through updateQueryData.
      createdSections.forEach((s) => {
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', s.tunnel_id, (rows) => {
            const row = rows.find((r) => r.id === s.id);
            if (row) {
              row.name_on_canvas = value;
              row.type = value;
            }
          })
        );
      });
      const stampedFirst = { ...firstSection, name_on_canvas: value, type: value };
      ahuBoardBus.emit('added:new:component', stampedFirst);
      ahuBoardBus.emit('show:on:canvas', stampedFirst, !!blocks[0].isCtrlPressed);
      ahuBoardBus.emit('add:new:component:form', component, value);
      void dispatch(validateOnNewComponent(componentId));
      const components = await dispatch(
        ahuBoardApi.endpoints.getSectionComponents.initiate(firstSection.id, {
          forceRefetch: true,
        })
      ).unwrap();
      ahuBoardBus.emit('component:created', components, value);
      // Server truth for the fields the stamp cannot supply — the derived
      // read-only ones (`name`, `label`, `components_qty`) AND `season_id`,
      // which the component POST rewrites: section_viewset stamps a brand-new
      // section from its own type, and at that moment the section is still a
      // bare plenum, so `default_season_id_for_type('plenum')` lands
      // "Summer + Winter" (id 3) in the 201 body we cached. The component POST
      // that follows re-stamps it per component kind
      // (epack_viewset.py:110-114 → 'summer' for an Evap Pack, coil/gas heater
      // likewise), but only server-side — the cached section row keeps the
      // plenum-era 3. Legacy never sees that intermediate: the component's
      // post_save re-serializes its AHUSection over AMQP and the syncdata
      // socket refreshes the Backbone model before the auto-opened selector
      // reads it. The React board has no section socket listener, so refetch —
      // and AWAIT it, because the selector this function is about to auto-open
      // seeds its section draft ONCE, from the row it is handed at open time
      // (EvapPackSelector's `seeded` guard). Firing this off unawaited handed
      // a freshly created Evap Pack's selector the stale row, so its Season
      // rendered "Summer + Winter" until the dialog was closed and reopened.
      await Promise.all(
        [...new Set(createdSections.map((s) => s.tunnel_id))].map((tunnelId) =>
          dispatch(
            ahuBoardApi.endpoints.getTunnelSectionList.initiate(tunnelId, {
              forceRefetch: true,
            })
          )
        )
      );
      if (AUTO_OPEN_SELECTOR.includes(value)) autoOpenSelector(dispatch, getState, firstSection.id);
    } catch {
      /* component create failed */
    }
  };

/** api.accessory.create (§9.3 — create:accessory:any auto-wire). */
export const createAccessoryAny =
  (name: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // apd_gage / uvc_system renames (§9.3).
    let type = name;
    if (type === 'apd_gage') type = 'adp_gage';
    if (type === 'uvc_system') type = 'uvc';
    // Combo Louver (KA-5245) — A0.1, change 2 of 3. api.js:373-378:
    //   var isCombo = (name == 'combo_louver'); if (isCombo) { name = 'louver'; }
    // "Combo Louver is a louver in combo mode -- it shares the louver model/
    //  dialog but is created with is_combo=true so the dialog opens straight into
    //  combo mode (KA-5245)."
    // Remapping HERE (not at the call sites) is what fixes every downstream
    // consumer at once — the plural lookup, setCurrentTab, setSelectedAccessory,
    // `select:accessory` and `added:new:accessory` all see 'louver', which is
    // exactly the post-remap `name` legacy emits (api.js:414/418).
    const isCombo = type === 'combo_louver';
    if (isCombo) type = 'louver';
    const sectionId = getState().ahuBoard.selection.currentSectionId;
    if (sectionId == null) {
      document.querySelector('.added-accss-note')?.classList.remove('active');
      return;
    }
    dispatch(setCurrentTab('accessories'));
    const plural = ACCESSORY_PLURALS[type];
    if (!plural) return;
    try {
      const created = await dispatch(
        ahuBoardFormsApi.endpoints.createConcreteRow.initiate({
          plural,
          // Combo Louver (KA-5245) — A0.1, change 3 of 3: the extra-creation-
          // fields seam. api.js:405-409 spreads
          //   ...(isCombo ? { is_combo: true, air_direction_id: 1 } : {})
          // onto the ConcreteAccessory. Measured wire body (inventory §4 W1):
          //   POST /api/v1.0/ahu/louvers/
          //   {"section_id":21785,"is_combo":true,"air_direction_id":1}
          // ★ `air_direction_id: 1` is LOAD-BEARING, not cosmetic: the model's
          // left/right face mirror is `swap = self.air_direction_id != 1`
          // (models_accessory_louver.py:1533), so a NULL air direction silently
          // mirrors the side louvers (inventory R3 — backend, reported not fixed).
          body: { section_id: sectionId, ...(isCombo ? { is_combo: true, air_direction_id: 1 } : {}) },
        })
      ).unwrap();
      track('accessory_added', { accessory_type: isCombo ? 'combo_louver' : type });
      const accessoryId = Number(created.id);
      // Legacy `data-new="true"` (tab.accessories.js:230-241) — arms the new
      // row's form for one full changeSelectValue pass, which is what filled
      // an accessory's defaults on add. See newlyAddedAccessoryIds.
      newlyAddedAccessoryIds.add(accessoryId);
      await dispatch(
        ahuBoardApi.endpoints.getSectionAccessories.initiate(sectionId, {
          forceRefetch: true,
        })
      ).unwrap();
      ahuBoardBus.emit('added:new:accessory', accessoryId, type);
      if (type !== 'user_defined_accessory') {
        void dispatch(validateOnNewAccessory(accessoryId));
      }
      ahuBoardBus.emit('added:new:component');
      if (type !== 'uvc') {
        dispatch(setSelectedAccessory(accessoryId));
        ahuBoardBus.emit('select:accessory', accessoryId);
      }
      // Lug recalc for the hosting section's tunnel (§9.3).
      const queries = (getState()[ahuBoardApi.reducerPath] as {
        queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
      }).queries;
      let hostTunnelId: number | null = null;
      Object.values(queries).forEach((q) => {
        if (q?.endpointName === 'getTunnelSectionList' && Array.isArray(q.data)) {
          const hit = (q.data as IAhuBoardSection[]).find((r) => r.id === sectionId);
          if (hit) hostTunnelId = hit.tunnel_id;
        }
      });
      if (hostTunnelId != null) {
        ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
          tunnel_id: hostTunnelId,
          hide_from_canvas: false,
        });
      }
    } catch {
      document.querySelector('.added-accss-note')?.classList.remove('active');
    }
  };

/** api.accessory.duplicate (§9.3 — duplicate:accessory:any auto-wire).
 * DEVIATION(6e2): the legacy stages `duplicated_accessory` in stateStorage
 * for the accessory FORM to apply; with forms landing in 6e2, the clone is
 * applied directly via PATCH here (same endpoints, same net writes). */
export const duplicateAccessoryAny =
  (
    sectionId: number,
    type: string,
    model: Record<string, unknown>,
    position: Record<string, unknown> | null,
    _sameSection: boolean,
    tunnelId: number
  ) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const plural = ACCESSORY_PLURALS[type];
    if (!plural) return;
    document.querySelector('.duplicated-accss-note')?.classList.add('active');
    try {
      const created = await dispatch(
        ahuBoardFormsApi.endpoints.createConcreteRow.initiate({
          plural,
          body: { section_id: sectionId },
        })
      ).unwrap();
      const accessoryId = Number(created.id);
      // Clone attrs minus server-managed keys + nulls (§9.3 strip list).
      const clone: Record<string, unknown> = {};
      Object.keys(model).forEach((key) => {
        if (DUPLICATE_STRIP_KEYS.includes(key)) return;
        const v = model[key];
        if (v == null) return;
        // api.js:463-467 — a numeric STRING goes over as `String(Number(v))`,
        // which is what turns the serializer's "24.00" into "24". Legacy guards
        // on `Number(v)` being truthy, so "0" and non-numeric strings pass
        // through untouched; keep that, `Number('')` being 0 makes the empty
        // string fall through the same way.
        const n = typeof v === 'string' ? Number(v) : NaN;
        clone[key] = n ? String(n) : v;
      });
      if (Object.keys(clone).length) {
        await dispatch(
          ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
            plural,
            id: accessoryId,
            data: clone,
          })
        ).unwrap();
      }
      if (position) {
        const posClone: Record<string, unknown> = {};
        Object.keys(position).forEach((key) => {
          if (DUPLICATE_POSITION_STRIP_KEYS.includes(key) || position[key] == null) return;
          posClone[key] = position[key];
        });
        if (Object.keys(posClone).length) {
          await dispatch(
            ahuBoardFormsApi.endpoints.patchAhuPosition.initiate({
              id: accessoryId,
              data: posClone,
            })
          )
            .unwrap()
            .catch(() => undefined);
        }
      }
      await dispatch(
        ahuBoardApi.endpoints.getSectionAccessories.initiate(sectionId, {
          forceRefetch: true,
        })
      ).unwrap();
      dispatch(setSelectedAccessory(accessoryId));
      ahuBoardBus.emit('select:accessory', accessoryId);
      ahuBoardBus.emit('added:new:accessory', accessoryId, type);
      void dispatch(validateOnNewAccessory(accessoryId));
      ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
        tunnel_id: tunnelId,
        hide_from_canvas: false,
      });
    } finally {
      document.querySelector('.duplicated-accss-note')?.classList.remove('active');
    }
  };
